import { isPlatformBrowser } from '@angular/common';
import { KeyValue } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { getFeatureToggle } from '@capital-access/common/feature-toggle';
import { Log } from '@capital-access/common/logging';
import { AppSettingsService } from '@capital-access/common/settings';
import { getAccountPreferences } from '@capital-access/common/user';

interface OneTrustWindow {
  OneTrust?: {
    ToggleInfoDisplay?: () => void;
  };
}

interface OneTrustSettings {
  oneTrust?: {
    domainScript: string;
    scriptUrl: string;
  };
  version: string;
}

@Injectable({
  providedIn: 'root'
})
export class CookieSettingsService {
  constructor(
    private appSettings: AppSettingsService,
    @Inject(PLATFORM_ID) private readonly platformId: object,
    private store: Store
  ) {}

  /**
   * Opens the OneTrust cookie settings panel
   */
  showCookieSettings(): void {
    try {
      // Use type assertion to safely access OneTrust
      const windowWithOneTrust = window as OneTrustWindow;
      const oneTrust = windowWithOneTrust.OneTrust;
      if (oneTrust && typeof oneTrust.ToggleInfoDisplay === 'function') {
        oneTrust.ToggleInfoDisplay();
      } else {
        Log.warn('OneTrust is not available or ToggleInfoDisplay is not a function');
      }
    } catch (error) {
      Log.error({ message: 'Error showing cookie settings panel', error });
    }
  }

  /**
   * Check if OneTrust is available and loaded
   */
  isOneTrustAvailable(): boolean {
    const windowWithOneTrust = window as OneTrustWindow;
    const oneTrust = windowWithOneTrust.OneTrust;
    return !!(oneTrust && typeof oneTrust.ToggleInfoDisplay === 'function');
  }

  shouldShowCookieBanner(featureToggleName: string = 'onetrust-cookie-banner'): Observable<boolean> {
    return this.store.select(getFeatureToggle(featureToggleName)).pipe(
      filter((featureToggle): featureToggle is boolean => typeof featureToggle === 'boolean'),
      switchMap(featureToggle => {
        if (!featureToggle) {
          return of(false);
        }

        return this.store.select(getAccountPreferences).pipe(
          filter((preferences): preferences is KeyValue<string, string>[] => !!preferences),
          take(1),
          map(preferences => {
            const brandingPref = preferences.find(p => p.key === 'application:branding');
            const isSnpBranding = brandingPref?.value === 'snp' || !brandingPref;

            // Both conditions must be true: feature toggle enabled AND branding is SNP
            return featureToggle && isSnpBranding;
          })
        );
      })
    );
  }

  initializeOneTrust(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.shouldShowCookieBanner().subscribe(shouldShow => {
      if (shouldShow) {
        this.loadOneTrustScript();
      } else {
        this.hideCookieSettingsButton();
      }
    });
  }

  loadOneTrustScript(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      const settings = this.appSettings.getSettings<OneTrustSettings>();
      const domainScript = settings.oneTrust?.domainScript;
      const scriptUrl = settings.oneTrust?.scriptUrl || 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js';

      if (!domainScript) {
        Log.warn('OneTrust domain script ID not found in settings');
        return;
      }

      if (this.isScriptAlreadyLoaded(domainScript)) {
        Log.info('OneTrust script already loaded');
        this.injectCookieBannerGlobalStyles();
        return;
      }

      this.createAndAppendScript(scriptUrl, domainScript);
      this.injectCookieBannerGlobalStyles();
    } catch (error) {
      Log.error({ message: 'Error loading OneTrust script', error });
    }
  }

  /**
   * Injects global styles for the OneTrust cookie banner
   */
  private injectCookieBannerGlobalStyles(): void {
    // Remove existing style if present to ensure it's always last in <head>
    const existing = document.getElementById('onetrust-custom-style');
    if (existing) {
      existing.remove();
    }
    const style = document.createElement('style');
    style.id = 'onetrust-custom-style';
    style.textContent = `
      #onetrust-consent-sdk #onetrust-pc-sdk *:focus,
      #onetrust-consent-sdk #onetrust-pc-sdk .ot-vlst-cntr > a:focus {
        outline: none !important;
        outline-offset: 0 !important;
      }

      #onetrust-consent-sdk #onetrust-banner-sdk *:focus, #onetrust-consent-sdk #onetrust-banner-sdk:focus {
        outline: none;
      }

      #onetrust-banner-sdk #onetrust-accept-btn-handler,
      #onetrust-banner-sdk #onetrust-reject-all-handler,
      #onetrust-banner-sdk #onetrust-pc-btn-handler {
        min-width: 160px !important;
      }

      #onetrust-consent-sdk #onetrust-pc-btn-handler,
      #onetrust-consent-sdk #onetrust-pc-btn-handler.cookie-setting-link {
        background-color: #D6002A !important;
        border: 2px solid #FFFFFF !important;
        color: #FFFFFF !important;
        text-decoration: none !important;
      }

      #ot-sdk-btn.ot-sdk-show-settings,
      #ot-sdk-btn.optanon-show-settings {
        background-color: transparent !important;
      }

      .btn.btn-link:hover:focus-visible {
        background-color: transparent !important;
      }

      #onetrust-banner-sdk #onetrust-policy {
        margin: 1em 0 1em 2em !important;
        overflow: hidden !important;
      }

      @media only screen and (min-width: 250px) and (max-width: 896px) {
        #onetrust-banner-sdk #onetrust-button-group {
          display: block !important;
        }
        #onetrust-banner-sdk #onetrust-button-group button {
          display: block;
          width: 100% !important;
        }
       #onetrust-banner-sdk #onetrust-policy {
        margin: 1em 0 1em 0em !important;
        overflow: hidden !important;
      }
        #onetrust-banner-sdk .ot-sdk-container,
        #onetrust-pc-sdk .ot-sdk-container,
        #ot-sdk-cookie-policy .ot-sdk-container {
          width: 100% !important;
        }
        #onetrust-banner-sdk:not(.ot-iab-2) #onetrust-group-container {
        width: 100% !important;
        }
  
        #onetrust-banner-sdk button:not(.ot-close-icon), 
        #onetrust-pc-sdk button:not(.ot-close-icon), 
        #ot-sdk-cookie-policy button:not(.ot-close-icon) {
          margin-bottom: 0.65rem !important;
        }
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Hides the cookie settings button
   */
  hideCookieSettingsButton(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // Use a timeout to ensure DOM is ready in case the button is rendered after this call
    setTimeout(() => {
      const button = document.getElementById('ot-sdk-btn');
      if (button) {
        button.style.display = 'none';
        Log.info('Cookie settings button hidden - feature toggle disabled');
      }
    }, 0);
  }

  private isScriptAlreadyLoaded(domainScript: string): boolean {
    return !!document.querySelector(`script[data-domain-script="${domainScript}"]`);
  }

  private createAndAppendScript(scriptUrl: string, domainScript: string): void {
    const script = document.createElement('script');
    script.src = scriptUrl;
    script.type = 'text/javascript';
    script.setAttribute('data-domain-script', domainScript);

    script.onload = () => Log.info('OneTrust script loaded successfully');
    script.onerror = () => Log.error('Failed to load OneTrust script');

    document.head.appendChild(script);
    Log.info(`Loading OneTrust script with domain: ${domainScript}`);
  }

  /**
   * Checks if the current branding is BNY
   */
  isBnyBranding(): boolean {
    let isBny = false;

    // Synchronously check DOM for branding attribute as a fallback
    if (isPlatformBrowser(this.platformId)) {
      const bodyElement = document.body;
      const brandingAttribute = bodyElement.getAttribute('data-branding');
      isBny = brandingAttribute?.includes('bny') || false;
    }

    return isBny;
  }
}
